import {
  Drawer,
  Modal,
  Spin,
  Table,
  Tooltip,
  Col,
  Row,
} from "antd";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import cx from "classnames";

import { getColumnSearchProps } from "../../../utils/tables";
import Header from "../../../components/HeaderList";
import OrderDetail from "../../../components/OrderDetail";
import styles from "../styles/MonitorUI.module.css";

import { ReactComponent as History } from "../../../assets/icons/history.svg";
import { ReactComponent as Warning } from "../../../assets/icons/warning.svg";
import { ReactComponent as EyeBox } from "../../../assets/icons/eye-box.svg";
import { ReactComponent as Preparing } from "../../../assets/icons/preparing.svg";
import { ReactComponent as OrderList } from "../../../assets/icons/order-list.svg";
import { ReactComponent as AssignedDriver } from "../../../assets/icons/assigned-driver.svg";
import { ReactComponent as DriverInStore } from "../../../assets/icons/driver-in-store.svg";
import { ReactComponent as OnTheWay } from "../../../assets/icons/on-the-way.svg";
import { ReactComponent as RecentOrder } from "../../../assets/icons/recent-order.svg";
import { ReactComponent as PreparingWhite } from "../../../assets/icons/preparing-white.svg";
import { ReactComponent as OrderListWhite } from "../../../assets/icons/order-list-white.svg";
import { ReactComponent as DriverWhite } from "../../../assets/icons/driver-white.svg";
import { useRef } from "react";


const NewMonitorUI = (props) => {
  const {
    modalOrder,
    loadingOrder,
    closeDetails,
    openDetails,
    loaded,
    isDriver,
    orderDetail,
    newOrders,
    dayOrders,
    branches,
    acceptOrder,
    updateOrder,
    assignOrder,
    finishOrder,
    cancelOrder,
    resendOrder,
    sendPeya,
    getDriversBranch,
    openDriverConfirm,
    transferOrder,
    reasons,
    currentPage,
    ordersTotal,
    filtersRef,
    loadingTable,
    onTableChange,
  } = props;

  const [orderType, setOrderType] = useState(-1);
  const searchInput = useRef(null);
  const [drawerWidth, setDrawerWidth] = useState(1150);

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth <= 768 ? "100%" : 1150);
    };

    handleResize(); // set on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getIcon = (status, orderType, type = "table") => {
    const icons = {
      7: type === "card" ? <PreparingWhite /> : <Preparing />,
      8: type === "card" ? <DriverWhite /> : <AssignedDriver />,
      9: type === "card" ? <DriverWhite /> : <DriverInStore />,
      10: type === "card" ? <OrderListWhite /> : <OrderList />,
      11:
        type === "card"
          ? orderType <= 1
            ? <OrderListWhite />
            : <DriverWhite />
          : orderType <= 1
          ? <OrderList />
          : <OnTheWay />,
      default: <RecentOrder />,
    };

    const titles = {
      7: "Preparando",
      8: "Driver asignado",
      9: "Driver en tienda",
      10: "Orden lista",
      11: orderType <= 1 ? "lista para recoger" : "En camino",
      default: "Orden nueva",
    };

    const icon = icons[status] || icons.default;
    const title = titles[status] || titles.default;

    return <Tooltip title={title}>{icon}</Tooltip>;
  };


  const orderStates = useMemo(() => {
    const counts = dayOrders.reduce(
      (acc, o) => ({ ...acc, [o.status]: (acc[o.status] || 0) + 1 }),
      {}
    );
    return [6, 7, 8, 9, 10, 11].map((val) => ({
      value: val,
      text: val === 11 ? (orderType <= 1 ? "lista para recoger" : "En camino") : {
        6: "Orden nueva",
        7: "Preparando",
        8: "Driver Asignado",
        9: "Driver en tienda",
        10: "Orden lista",
      }[val],
      items: counts[val] || 0,
    }));
  }, [dayOrders, orderType]);

  const columns = useMemo(() => [
    {
      title: "Origen",
      dataIndex: "platformName",
      align: "center",
    },
    {
      title: "Ticket",
      dataIndex: "ticket",
      ...getColumnSearchProps("ticket", searchInput, filtersRef, false),
      render: (val, record) => (
        <span>
          {(!record.cireba_remote_id || record.cireba_remote_id === "0") && (
            <Tooltip title="Orden no ingresada a CIREBA">
              <Warning className={styles.warning} />
            </Tooltip>
          )}
          {val}
        </span>
      ),
    },
    {
      title: "Cliente",
      dataIndex: "id_user__fullname",
      ...getColumnSearchProps("customerName", searchInput, filtersRef, false)
    },
    {
      title: "Sucursal",
      dataIndex: "branch",
      align: "center",
      render: (val) => val.zone || "-",
      ...getColumnSearchProps("branch_name", searchInput, filtersRef, false)
    },
    {
      title: "Hora de ingreso",
      dataIndex: "created_time",
      align: "center",
      render: (val) => moment(val).format("hh:mm a"),
    },
    {
      title: "Teléfono",
      dataIndex: "id_user",
      align: "center",
      render: (val) => val.mobile,
      ...getColumnSearchProps("phone", searchInput, filtersRef, false)
    },
    {
      title: "Monto actual",
      dataIndex: "total",
      align: "center",
      render: (val) => `$${val}`,
    },
    {
      title: "Estado",
      dataIndex: "status",
      align: "center",
      filters: orderStates,
      onFilter: (val, record) => record.status === val,
      render: (val, record) => getIcon(val, record.orderType),
      filterMultiple: false,
    },
    {
      title: "Tipo de Orden",
      dataIndex: "orderTypeName",
      align: "center",
      filters: [
        "PEDIDOS YA",
        "UBER EATS",
        "COMER AQUI",
        "PARA LLEVAR",
        "DOMICILIO",
      ].map((val) => ({ value: val, text: val })),
      onFilter: (val, record) => record.orderTypeName === val,
      filterMultiple: false,
    },
    {
      title: "Ver detalle",
      dataIndex: "id",
      align: "center",
      render: (val) => (
        <button
          disabled={loadingOrder}
          onClick={() => openDetails(val)}
          className={styles.eye}
        >
          {loadingOrder === val ? <Spin size="small" /> : <EyeBox />}
        </button>
      ),
    },
  ], [dayOrders, orderStates, loadingOrder]);

  if (!loaded) {
    return (
      <div className={cx(styles.view, styles.loader)}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.view}>
      <Row>
        <Col span={24}>
          <Header
            title="Ordenes Activas"
            actions={
              <div className={styles.ordersButtons}>
                {/* 
                <Link to="/drivers" className={styles.history}><span> 📡 DRIVERS EN LINEA</span></Link>
                */}
                <Link to="/historial" className={styles.history}><History /><span>VER HISTORIAL DE ORDENES</span></Link>
              </div>
            }
          />
          <Table loading={loadingTable} columns={columns} dataSource={dayOrders} rowKey="id" pagination={{ current: currentPage, pageSize: 10, total: ordersTotal }} onChange={onTableChange} />
        </Col>
      </Row>
      

      <Drawer
        destroyOnClose
        placement="right"
        visible={modalOrder}
        width={drawerWidth}
        closable={true}
        onClose={closeDetails}
        className={styles.modalOrder}
      >
        {orderDetail && (
          <OrderDetail
            order={orderDetail}
            monitor
            branches={branches}
            acceptOrder={acceptOrder}
            updateOrder={updateOrder}
            assignOrder={assignOrder}
            finishOrder={finishOrder}
            cancelOrder={cancelOrder}
            resendOrder={resendOrder}
            sendPeya={sendPeya}
            getDriversBranch={getDriversBranch}
            openDriverConfirm={openDriverConfirm}
            transferOrder={transferOrder}
            reasons={reasons}
          />
        )}
      </Drawer>

    </div>
  );
};

export default NewMonitorUI;
